import api from "./axios";

/**
 * Start a redirect-based payment. Backend creates a PENDING transaction,
 * returns the hosted-page URL the SPA must navigate to.
 *
 * @param {object} args
 * @param {"RENEWAL"|"ADDON_PURCHASE"} args.purpose
 * @param {string} [args.addOnCode] - required when purpose === "ADDON_PURCHASE"
 * @param {string} [args.planCode]  - optional for RENEWAL: target plan code. If different
 *                                    from current plan, the subscription switches plan during finalize.
 * @param {string} [args.returnPath] - SPA path to navigate back to after callback resolves
 * @param {string} [args.language]
 * @returns {Promise<{paymentTransactionId:number, redirectUrl:string, externalReference:string, amount:number, currency:string}>}
 */
export async function initiatePayment({ purpose, addOnCode, planCode, returnPath, language }) {
  const res = await api.post("/payments/initiate", {
    purpose,
    addOnCode,
    planCode,
    returnPath,
    language,
  });
  return res.data;
}

/**
 * Verify and capture a previously initiated payment after the customer returns from the HPP.
 * Idempotent — safe to call multiple times.
 *
 * @param {number|string} paymentTransactionId
 * @returns {Promise<{success:boolean, status:string, purpose:string, returnPath:string|null, failureCode:string|null, failureMessage:string|null}>}
 */
export async function finalizePayment(paymentTransactionId) {
  const res = await api.post(`/payments/${paymentTransactionId}/finalize`);
  return res.data;
}

/**
 * The current tenant's payment history — both successful and unsuccessful attempts, newest first.
 *
 * @returns {Promise<Array<{id:number, amount:number, currency:string, status:"SUCCEEDED"|"FAILED"|"PENDING"|"REFUNDED", purpose:string, itemName:string|null, gatewayReference:string|null, failureMessage:string|null, createdAt:string, completedAt:string|null}>>}
 */
export async function getPaymentTransactions() {
  const res = await api.get("/payments/transactions");
  return res.data;
}
